import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, User, Search, Filter, Download, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SpeakersPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [cmsSpeakers, setCmsSpeakers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch CMS speakers on mount
  useEffect(() => {
    const fetchSpeakers = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cms/speakers`);
        if (response.ok) {
          const data = await response.json();
          setCmsSpeakers(data);
        }
      } catch (err) {
        console.error('Failed to fetch CMS speakers:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSpeakers();
  }, []);

  // Default hardcoded speakers (fallback)
  const defaultSpeakers = [
    {
      name: 'Vernand Morency',
      role: 'Conference Chair & Harvard Fellow',
      affiliation: 'Edgewood University',
      image: 'https://customer-assets.emergentagent.com/job_ea17ed55-887b-400a-8525-513b1791bc1e/artifacts/ox1jdeul_WhatsApp%20Image%202026-02-03%20at%2006.19.07%20%281%29.jpeg',
      bio: 'Vernand Morency is a Harvard Fellow and doctoral researcher focusing on the intersection of sports entertainment economics, public policy, and social impact. A former NFL running back and professional baseball player with the Colorado Rockies organization, Morency brings unique insight into the massive economic engine of sports entertainment. He is the architect of the "Jock Tax Housing Trust" proposal.',
      tags: ['Keynote Speaker', 'Organizer', 'Policy'],
      isKeynote: true,
      order: 1
    },
    {
      name: 'Dr. Peter Linneman',
      role: 'Principal & Professor Emeritus',
      affiliation: 'Wharton School',
      image: 'https://customer-assets.emergentagent.com/job_affordhome-1/artifacts/qf0533yk_peter-linneman.jpg',
      imagePosition: 'center 20%',
      bio: 'Dr. Peter Linneman is a leading authority on real estate strategy and finance. As the Albert Sussman Professor Emeritus of Real Estate, Finance, and Public Policy at the Wharton School, he has shaped the industry\'s understanding of market dynamics.',
      tags: ['Moderator', 'Real Estate Economics'],
      isKeynote: true,
      order: 2
    },
    {
      name: 'Ben Rubenstein',
      role: 'Founder',
      affiliation: 'Setpoint.io',
      image: null,
      bio: 'Ben Rubenstein is a serial entrepreneur and the founder of Setpoint.io. He is revolutionizing the infrastructure of real estate finance by building the "operating system" for asset backed lending. He focuses on the fintech rails that allow capital to flow with speed and transparency.',
      tags: ['Panelist', 'Proptech', 'Fintech Infrastructure'],
      order: 3
    },
    {
      name: 'Chris Herbert',
      role: 'Managing Director',
      affiliation: 'Harvard Joint Center for Housing Studies',
      image: null,
      bio: 'Dr. Chris Herbert is the Managing Director of the Harvard Joint Center for Housing Studies. He has extensive experience in conducting research on housing policy and urban development, both in the U.S. and abroad. His work focuses on the financial and demographic dimensions of homeownership and the rental housing market.',
      tags: ['Panelist', 'Housing Policy', 'Research', 'Harvard'],
      order: 4
    },
    {
      name: 'Christine Kwak',
      role: 'Co-Founder',
      affiliation: 'KL Realty',
      image: 'https://customer-assets.emergentagent.com/job_affordhome-1/artifacts/8p346fsd_christine-kwak.jpg',
      bio: 'Christine Kwak is a Co-Founder of KL Realty, a real estate development firm focused on sustainable community development. She brings expertise in housing development and infrastructure investment strategies.',
      tags: ['Panelist', 'Real Estate', 'Development'],
      order: 5
    },
    {
      name: 'Dr. David Harris',
      role: 'Managing Director Emeritus',
      affiliation: 'Charles Hamilton Houston Institute for Race and Justice, Harvard Law School',
      image: null,
      bio: 'Dr. David Harris is a renowned expert on the intersection of law, race, and social justice. As the longtime leader of the Charles Hamilton Houston Institute, he has championed civil rights initiatives that address systemic inequalities. His work emphasizes the role of policy in creating equitable communities.',
      tags: ['Panelist', 'Civil Rights', 'Social Justice', 'Harvard'],
      order: 6
    },
    {
      name: 'Ed Glaeser',
      role: 'Fred and Eleanor Glimp Professor of Economics',
      affiliation: 'Harvard University',
      image: null,
      bio: 'Edward Glaeser is one of the world\'s most influential urban economists. He has published dozens of papers on cities, economic growth, and law and economics. His book "Triumph of the City" is a definitive text on the value of urban density. He brings critical academic rigor to the housing supply debate.',
      tags: ['Panelist', 'Urban Economics', 'Harvard'],
      order: 7
    },
    {
      name: 'Jason Wright',
      role: 'Former President',
      affiliation: 'Washington Commanders',
      image: null,
      bio: 'Jason Wright made history as the first Black president of an NFL team. Before leading the Commanders, he was a partner at McKinsey & Company. He brings a unique "triple threat" perspective as a former NFL player, top tier consultant, and team executive, understanding the C-suite view of league economics.',
      tags: ['Panelist', 'Executive Leadership', 'NFL Alum', 'Athlete'],
      order: 8
    },
    {
      name: 'Jeremy Lin',
      role: 'Professional Athlete & Philanthropist',
      affiliation: 'Harvard \'10',
      image: null,
      bio: 'Jeremy Lin is a professional basketball player and Harvard economics graduate who sparked the global "Linsanity" phenomenon. Beyond the court, he is a thoughtful advocate for social justice and community empowerment. He brings the perspective of a modern athlete navigating global tax jurisdictions.',
      tags: ['Panelist', 'Athlete', 'Harvard', 'Philanthropy'],
      order: 9
    },
    {
      name: 'Magic Johnson (Invited)',
      role: 'Chairman & CEO',
      affiliation: 'Magic Johnson Enterprises',
      image: null,
      bio: 'Earvin "Magic" Johnson is the Chairman and CEO of Magic Johnson Enterprises, an investment conglomerate that provides high quality products and services to ethnically diverse and underserved urban communities. He is a global leader in urban redevelopment and a Hall of Fame NBA legend.',
      tags: ['Keynote Speaker', 'Urban Development', 'Philanthropy', 'Athlete'],
      order: 10
    },
    {
      name: 'Mo Vaughn',
      role: 'Co-Founder & Managing Director',
      affiliation: 'Omni New York LLC',
      image: null,
      bio: 'Mo Vaughn is a former MLB MVP and three time All Star who has become a powerhouse in affordable housing. As co-founder of Omni New York LLC, he has rehabilitated over 10,000 units of affordable housing. He exemplifies the impact of developers leveraging Jock Tax capital to revitalize communities.',
      tags: ['Panelist', 'Developer', 'MLB Legend', 'Athlete'],
      order: 11
    },
    {
      name: 'Parfait Atchadé',
      role: 'Research Affiliate',
      affiliation: 'MIT Media Lab, City Science',
      image: 'https://customer-assets.emergentagent.com/job_affordhome-1/artifacts/y0309zzu_parfait-atchade.png',
      bio: 'Dr. Parfait Atchadé is a Research Affiliate at MIT Media Lab, City Science. He pioneers "Humanized Agent Based Models" to solve complex urban challenges, bridging the gap between quantum algorithms and social equity.',
      tags: ['Panelist', 'Quantum Computing', 'MIT Media Lab', 'Technology'],
      order: 12
    }
  ];

  // Merge CMS speakers with defaults - CMS takes priority if available
  const allSpeakers = useMemo(() => {
    if (cmsSpeakers.length > 0) {
      // Create a map of default speakers for tag lookup
      const defaultMap = {};
      defaultSpeakers.forEach(s => {
        defaultMap[s.name.toLowerCase()] = s;
      });
      
      // Transform CMS data, preserving tags from defaults if available
      return cmsSpeakers.map(s => {
        const defaultData = defaultMap[s.name.toLowerCase()];
        return {
          name: s.name,
          role: s.title || defaultData?.role || '',
          affiliation: s.organization || defaultData?.affiliation || '',
          image: s.photo_url || defaultData?.image,
          imagePosition: s.image_position || defaultData?.imagePosition || 'center top',
          bio: s.bio || defaultData?.bio || '',
          tags: defaultData?.tags || (s.is_featured ? ['Keynote Speaker'] : ['Panelist']),
          isKeynote: s.is_featured || defaultData?.isKeynote || false,
          order: s.order
        };
      });
    }
    return defaultSpeakers;
  }, [cmsSpeakers]);

  // Get all unique tags
  const allTags = useMemo(() => {
    const tags = new Set();
    allSpeakers.forEach(speaker => {
      speaker.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, [allSpeakers]);

  // Filter speakers based on search and tags
  const filteredSpeakers = useMemo(() => {
    return allSpeakers
      .filter(speaker => {
        const matchesSearch = searchQuery === '' || 
          speaker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          speaker.affiliation.toLowerCase().includes(searchQuery.toLowerCase()) ||
          speaker.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
          speaker.bio.toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesTags = selectedTags.length === 0 ||
          selectedTags.some(tag => speaker.tags.includes(tag));
        
        return matchesSearch && matchesTags;
      })
      .sort((a, b) => a.order - b.order);
  }, [allSpeakers, searchQuery, selectedTags]);

  // Get keynote/featured speakers - limit to 3 and sort by order
  const keynoteSpeakers = filteredSpeakers
    .filter(s => s.isKeynote)
    .sort((a, b) => a.order - b.order)
    .slice(0, 3);
  const otherSpeakers = filteredSpeakers.filter(s => !s.isKeynote || !keynoteSpeakers.find(k => k.name === s.name));

  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTags([]);
  };

  const getTagColor = (tag) => {
    if (tag.includes('Keynote')) return 'bg-primary/20 text-primary';
    if (tag.includes('Moderator')) return 'bg-secondary/20 text-secondary';
    if (tag.includes('Harvard') || tag.includes('MIT') || tag.includes('Wharton')) return 'bg-purple-500/20 text-purple-400';
    if (tag.includes('Athlete') || tag.includes('NFL') || tag.includes('MLB')) return 'bg-green-500/20 text-green-400';
    if (tag.includes('Panelist')) return 'bg-accent/20 text-accent';
    return 'bg-muted text-muted-foreground';
  };

  const generatePDF = () => {
    // Create printable content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Global Affordable Housing Summit - Speakers</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #0891b2; border-bottom: 2px solid #0891b2; padding-bottom: 10px; }
          h2 { color: #f59e0b; margin-top: 30px; }
          .speaker { margin-bottom: 30px; page-break-inside: avoid; }
          .speaker-name { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
          .speaker-role { color: #0891b2; font-size: 14px; margin-bottom: 5px; }
          .speaker-affiliation { color: #666; font-size: 14px; margin-bottom: 10px; }
          .speaker-bio { color: #333; font-size: 13px; line-height: 1.6; }
          .tags { margin-top: 8px; }
          .tag { display: inline-block; background: #e5e5e5; padding: 2px 8px; border-radius: 4px; font-size: 11px; margin-right: 5px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>Global Affordable Housing Summit</h1>
        <p>April 17th, 2026 | Tsai Auditorium, Harvard University</p>
        
        <h2>Keynote Speakers</h2>
        ${keynoteSpeakers.map(s => `
          <div class="speaker">
            <div class="speaker-name">${s.name}</div>
            <div class="speaker-role">${s.role}</div>
            <div class="speaker-affiliation">${s.affiliation}</div>
            <div class="speaker-bio">${s.bio}</div>
            <div class="tags">${s.tags.map(t => `<span class="tag">${t}</span>`).join('')}</div>
          </div>
        `).join('')}
        
        <h2>Featured Speakers</h2>
        ${otherSpeakers.map(s => `
          <div class="speaker">
            <div class="speaker-name">${s.name}</div>
            <div class="speaker-role">${s.role}</div>
            <div class="speaker-affiliation">${s.affiliation}</div>
            <div class="speaker-bio">${s.bio}</div>
            <div class="tags">${s.tags.map(t => `<span class="tag">${t}</span>`).join('')}</div>
          </div>
        `).join('')}
        
        <div class="footer">
          <p>Organized by Global InTech AS | globalintech.ai</p>
          <p>Register at affordhome.com/signup</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const SpeakerCard = ({ speaker, isLarge = false }) => (
    <div
      className={`glass-card rounded-xl overflow-hidden hover:border-primary/50 transition-all duration-300 group ${
        isLarge ? 'flex flex-col md:flex-row' : ''
      }`}
      data-testid={`speaker-card-${speaker.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Image */}
      <div className={`${isLarge ? 'md:w-1/3' : 'aspect-square'} overflow-hidden bg-muted relative`}>
        {speaker.image ? (
          <img
            src={speaker.image}
            alt={speaker.name}
            className={`w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 ${
              isLarge ? 'md:h-full min-h-[300px]' : ''
            }`}
            style={{ objectPosition: speaker.imagePosition || 'center top' }}
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50 ${
            isLarge ? 'min-h-[300px]' : ''
          }`}>
            <User className="w-20 h-20 text-muted-foreground/30" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className={`p-6 ${isLarge ? 'md:w-2/3 md:p-8' : ''}`}>
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          {speaker.tags.map((tag, i) => (
            <span
              key={i}
              className={`px-2 py-0.5 text-xs font-medium rounded-full cursor-pointer hover:opacity-80 ${getTagColor(tag)}`}
              onClick={() => toggleTag(tag)}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Name */}
        <h3 className={`font-outfit font-bold mb-1 ${isLarge ? 'text-2xl md:text-3xl' : 'text-xl'}`}>
          {speaker.name}
        </h3>

        {/* Role & Affiliation */}
        <p className="text-sm text-primary font-medium mb-1 uppercase tracking-wide">
          {speaker.role}
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          {speaker.affiliation}
        </p>

        {/* Bio */}
        <p className={`text-muted-foreground leading-relaxed ${isLarge ? '' : 'text-sm line-clamp-4'}`}>
          {speaker.bio}
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 mb-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
            Meet Our Visionaries
          </span>
          <h1 className="font-outfit text-4xl md:text-6xl font-bold tracking-tight mb-6" data-testid="speakers-title">
            Conference <span className="text-gradient-quantum">Speakers</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Learn from world class experts in affordable housing, impact investing, 
            urban economics, and sustainable development.
          </p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="px-6 md:px-12 lg:px-24 mb-12" data-testid="speaker-filters">
        <div className="max-w-6xl mx-auto">
          <div className="glass-card rounded-xl p-4 md:p-6">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 w-full md:max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search speakers by name, role, or affiliation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                  data-testid="speaker-search-input"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
                    showFilters || selectedTags.length > 0
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                  data-testid="toggle-filters-btn"
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {selectedTags.length > 0 && (
                    <span className="ml-1 px-2 py-0.5 text-xs bg-white/20 rounded-full">
                      {selectedTags.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={generatePDF}
                  className="flex items-center gap-2 px-4 py-3 bg-secondary text-secondary-foreground rounded-xl font-medium transition-all hover:-translate-y-0.5"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Download PDF</span>
                </button>
              </div>
            </div>

            {/* Tag Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-border animate-in slide-in-from-top-2 duration-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium">Filter by Tags</span>
                  {selectedTags.length > 0 && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                    >
                      <X className="w-3 h-3" />
                      Clear all
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                        selectedTags.includes(tag)
                          ? 'bg-primary text-primary-foreground'
                          : `${getTagColor(tag)} hover:opacity-80`
                      }`}
                      data-testid={`filter-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Active Filters Display */}
            {(searchQuery || selectedTags.length > 0) && (
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                <span>Showing {filteredSpeakers.length} of {allSpeakers.length} speakers</span>
                {(searchQuery || selectedTags.length > 0) && (
                  <button
                    onClick={clearFilters}
                    className="text-primary hover:underline"
                  >
                    Reset
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Hosts and Moderators - 3 in a row */}
      {keynoteSpeakers.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 mb-20" data-testid="keynote-speakers">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-outfit text-2xl font-semibold text-center mb-12">
              <span className="text-gradient-housing">Hosts</span> and Moderators
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {keynoteSpeakers.map((speaker, index) => (
                <SpeakerCard key={index} speaker={speaker} isLarge={false} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Speakers */}
      {otherSpeakers.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 mb-20" data-testid="featured-speakers">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-outfit text-2xl font-semibold text-center mb-12">
              Featured <span className="text-gradient-quantum">Speakers</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {otherSpeakers.map((speaker, index) => (
                <SpeakerCard key={index} speaker={speaker} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* No Results */}
      {filteredSpeakers.length === 0 && (
        <section className="px-6 md:px-12 lg:px-24 mb-20">
          <div className="max-w-2xl mx-auto text-center py-12">
            <User className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-outfit text-xl font-semibold mb-2">No speakers found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search or filter criteria.
            </p>
            <button
              onClick={clearFilters}
              className="text-primary hover:underline font-medium"
            >
              Clear all filters
            </button>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center glass-card rounded-3xl p-12">
          <h2 className="font-outfit text-3xl font-bold mb-4">
            Learn From the Best
          </h2>
          <p className="text-muted-foreground mb-8">
            Register now to attend sessions with our world class speakers and network with industry leaders.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1 glow-primary"
            >
              Register Now
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/agenda"
              className="inline-flex items-center gap-2 px-8 py-4 glass-card rounded-full font-semibold transition-all hover:bg-white/10"
            >
              View Full Agenda
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SpeakersPage;
